import { inject, Service, InjectionToken } from '@angular/core'

import { ApiError } from '../api/utils/fetch-utils'

export type FrontendLogLevel = 'debug' | 'info' | 'warn' | 'error'

const ORDER: Record<FrontendLogLevel, number> = { debug: 10, info: 20, warn: 30, error: 40 }

/** localStorage key that raises the console threshold at runtime, without a rebuild. */
export const LOG_LEVEL_STORAGE_KEY = 'seiri:log'

/**
 * Where log records go besides the console. Kept as an injected port rather than importing Sentry
 * here so that bundles which do not want Sentry - the embeddable elements build - are not forced
 * to carry it.
 */
export interface LogSink {
  breadcrumb(level: FrontendLogLevel, message: string, data?: Record<string, unknown>): void
  capture(error: unknown, data?: Record<string, unknown>): void
}

export const LOG_SINK = new InjectionToken<LogSink>('seiri.log.sink')

/** Console threshold for the app. Anything below this is still recorded to the sink. */
export const LOG_LEVEL = new InjectionToken<FrontendLogLevel>('seiri.log.level')

function readOverride(): FrontendLogLevel | undefined {
  try {
    const value = window.localStorage.getItem(LOG_LEVEL_STORAGE_KEY)?.trim().toLowerCase()
    return value && value in ORDER ? (value as FrontendLogLevel) : undefined
  } catch {
    // Private mode, blocked storage, SSR - the override is a convenience, never a requirement.
    return undefined
  }
}

/**
 * Application logger. Mirrors the backend policy: everything is *recorded* (as a breadcrumb, so it
 * rides along with the next captured error), while the console only shows what meets the
 * threshold. Raise the threshold in a deployed environment with
 * `localStorage.setItem('seiri:log', 'debug')` - frontend env vars are baked in at build time, so
 * this is the only way to get detail without a redeploy.
 */
@Service()
export class LoggerService {
  private readonly sink = inject(LOG_SINK, { optional: true })
  private readonly threshold = ORDER[readOverride() ?? inject(LOG_LEVEL, { optional: true }) ?? 'warn']

  /** Scopes messages to a component or service, the frontend analogue of `new Logger(Foo.name)`. */
  forContext(context: string): ScopedLogger {
    return new ScopedLogger(this, context)
  }

  debug(message: string, data?: Record<string, unknown>): void {
    this.write('debug', message, data)
  }

  info(message: string, data?: Record<string, unknown>): void {
    this.write('info', message, data)
  }

  warn(message: string, data?: Record<string, unknown>): void {
    this.write('warn', message, data)
  }

  /**
   * Reports a failure. The error object is passed separately from the message so the sink can
   * preserve its stack - `error.message` alone loses the only thing worth having.
   *
   * A request that got no response at all is recorded as a warning, not captured: a visitor on
   * bad mobile data is not an incident anyone can fix, and one Sentry issue per dropped request
   * buries the ones that are. It still rides along, with its reqId, as a breadcrumb on the next
   * real error. An outage shows up in the backend's own monitoring, not here.
   *
   * A refusal the API states with a code (already registered, event full - `ApiError.isRefusal`)
   * is recorded the same way: the user is told why, and nothing in the app is broken. So is an
   * expired session (`ApiError.isUnauthenticated`), which signs the user out.
   *
   * An uncaught network failure or expired session is dropped by `tagApiError` (beforeSend).
   */
  error(message: string, error?: unknown, data?: Record<string, unknown>): void {
    if (error instanceof ApiError && (error.isNetworkFailure || error.isRefusal || error.isUnauthenticated)) {
      this.write('warn', message, { ...data, reqId: error.reqId, code: error.code }, error)
      return
    }
    this.write('error', message, data, error)
  }

  private write(level: FrontendLogLevel, message: string, data?: Record<string, unknown>, error?: unknown): void {
    // Recorded regardless of the console threshold, so a captured error carries the trail that
    // led to it even when nothing was printed.
    this.sink?.breadcrumb(level, message, data)

    if (level === 'error') {
      this.sink?.capture(error ?? new Error(message), { ...data, message })
    }

    if (ORDER[level] < this.threshold) return

    const args: unknown[] = [message]
    if (data) args.push(data)
    if (error !== undefined) args.push(error)

    /* eslint-disable no-console -- this is the one place a console call belongs */
    const method =
      level === 'debug'
        ? console.debug
        : level === 'info'
          ? console.info
          : level === 'warn'
            ? console.warn
            : console.error
    method.apply(console, args)
    /* eslint-enable no-console */
  }
}

/** A logger bound to a context string; messages are prefixed so they are greppable. */
export class ScopedLogger {
  constructor(
    private readonly logger: LoggerService,
    private readonly context: string,
  ) {}

  debug(message: string, data?: Record<string, unknown>): void {
    this.logger.debug(`${this.context} ${message}`, data)
  }

  info(message: string, data?: Record<string, unknown>): void {
    this.logger.info(`${this.context} ${message}`, data)
  }

  warn(message: string, data?: Record<string, unknown>): void {
    this.logger.warn(`${this.context} ${message}`, data)
  }

  error(message: string, error?: unknown, data?: Record<string, unknown>): void {
    this.logger.error(`${this.context} ${message}`, error, data)
  }
}
